import { ChangeEvent, FC } from 'react';

import { DOT } from 'utils/constants/common';

import {
    Cell,
    Checkbox,
    HSpacing,
    TableBody,
    TableCell,
    TableRow,
    Text,
    useBreakpoint,
    VSpacing,
} from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import { MAX_VACANCY_SELECT } from 'components/MultiCreationAuction/VacanciesTable/VacanciesTable';

import { setMultiCreationSelectedVacancyIds, Vacancy } from 'store/models/multiCreation';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/MultiCreationAuction/VacanciesTable/VacanciesTableBody/VacanciesTableBody.module.less';

const TrlKeys = {
    id: 'MultiCreationAuction.VacanciesTable.VacanciesTableBody.id',
};

export const VacanciesTableBody: FC<{ paddingLeft: number; paddingRight: number }> = ({
    paddingLeft,
    paddingRight,
}) => {
    const vacancies = useSelector(({ multiCreation }) => multiCreation.vacancies);
    const selectedVacancyIds = useSelector(({ multiCreation }) => multiCreation.selectedVacancyIds);

    const { isMobile } = useBreakpoint();
    const trls = useTrls();
    const dispatch = useDispatch();

    const isCheckboxDisabled = (vacancyId: number) =>
        Object.keys(selectedVacancyIds).length >= MAX_VACANCY_SELECT && !selectedVacancyIds[vacancyId];

    const onVacancyCheckboxToggle = (event: ChangeEvent<HTMLInputElement>, vacancyId: number) => {
        const checked = event.target.checked;
        const newSelectedVacanciesIds = { ...selectedVacancyIds };
        if (checked) {
            newSelectedVacanciesIds[vacancyId] = true;
            dispatch(setMultiCreationSelectedVacancyIds(newSelectedVacanciesIds));
        } else {
            delete newSelectedVacanciesIds[vacancyId];
            dispatch(setMultiCreationSelectedVacancyIds(newSelectedVacanciesIds));
        }
    };

    const firstCell = (vacancy: Vacancy) => {
        const { vacancyId, name, areaName } = vacancy;
        return (
            <Cell
                vertPadding={isMobile}
                align="top"
                left={
                    <Checkbox
                        data-qa={`vacancy-checkbox-${vacancyId}`}
                        checked={!!selectedVacancyIds[vacancyId]}
                        onChange={(e) => onVacancyCheckboxToggle(e, vacancyId)}
                        disabled={isCheckboxDisabled(vacancyId)}
                    />
                }
            >
                <Text maxLines={1} typography="label-2-regular" data-qa="vacancy-name">
                    {name}
                </Text>
                <VSpacing default={4} />
                <Text maxLines={1} typography="label-3-regular" style="secondary">
                    {trls(TrlKeys.id, [vacancyId])}
                    <HSpacing default={4} />
                    {isMobile && (
                        <>
                            {DOT}
                            <HSpacing default={4} />
                            {areaName}
                        </>
                    )}
                </Text>
            </Cell>
        );
    };

    return isMobile ? (
        <>
            {vacancies.map((vacancy) => (
                <div key={vacancy.vacancyId} className={styles.mobileRow} style={{ paddingLeft, paddingRight }}>
                    {firstCell(vacancy)}
                </div>
            ))}
        </>
    ) : (
        <TableBody>
            {vacancies.map((vacancy) => (
                <TableRow key={vacancy.vacancyId} data-qa={`vacancy-row-${vacancy.vacancyId}`}>
                    <TableCell m={12}>
                        <div className={styles.rowWrapper}>
                            <div className={styles.nameColumn}>{firstCell(vacancy)}</div>
                            <div className={styles.areaColumn}>
                                <Text maxLines={1} data-qa="area-name">
                                    {vacancy.areaName}
                                </Text>
                            </div>
                            <div className={styles.managerColumn}>
                                <Text maxLines={1} data-qa="manager-name">
                                    {vacancy.managerName}
                                </Text>
                            </div>
                        </div>
                    </TableCell>
                </TableRow>
            ))}
        </TableBody>
    );
};
